import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const ChurchAnalyticsPageNew = () => {
  const { user } = useAuth();
  const { church } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30d');
  const [analytics, setAnalytics] = useState({
    totalEvents: 0,
    totalRegistrations: 0,
    totalRevenue: 0,
    avgRegistrationsPerEvent: 0,
    eventsByStatus: {},
    registrationsByMonth: [],
    revenueByMonth: [],
    topEvents: [],
    totalPageViews: 0,
    uniqueVisitors: 0,
    viewsOverTime: [],
    conversionRate: 0,
    avgTicketPrice: 0,
    growthRate: 0
  });

  useEffect(() => {
    fetchAnalytics();
  }, [user, church, dateRange]);

  const fetchAnalytics = async () => {
    if (!user || !church?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);

    try {
      // Fetch church's events
      let eventsQuery = supabase
        .from('events')
        .select('*')
        .or(`church_id.eq.${church.id},created_by.eq.${user.id}`);

      if (dateRange !== 'all') {
        const startDate = getStartDate(dateRange);
        eventsQuery = eventsQuery.gte('created_at', startDate.toISOString());
      }

      const { data: events } = await eventsQuery;
      const eventsList = events || [];

      // Count events by status
      const eventsByStatus = eventsList.reduce((acc, event) => {
        acc[event.status] = (acc[event.status] || 0) + 1;
        return acc;
      }, {});

      // Fetch registrations
      const eventIds = eventsList.map(e => e.id);
      let registrations = [];

      if (eventIds.length > 0) {
        let regsQuery = supabase
          .from('event_registrations')
          .select('*, events(title, start_date)')
          .in('event_id', eventIds);

        if (dateRange !== 'all') {
          const startDate = getStartDate(dateRange);
          regsQuery = regsQuery.gte('created_at', startDate.toISOString());
        }

        const { data: regs } = await regsQuery;
        registrations = regs || [];
      }

      // Calculate revenue
      const totalRevenue = registrations
        .filter(r => r.payment_status === 'paid')
        .reduce((sum, r) => sum + (r.amount_paid || 0), 0);

      // Calculate average ticket price
      const paidRegistrations = registrations.filter(r => r.payment_status === 'paid' && r.amount_paid > 0);
      const avgTicketPrice = paidRegistrations.length > 0 
        ? totalRevenue / paidRegistrations.length 
        : 0;

      // Group registrations by month
      const registrationsByMonth = groupByMonth(registrations, 'created_at');
      const revenueByMonth = groupRevenueByMonth(registrations);

      // Calculate growth rate
      const growthRate = calculateGrowthRate(registrationsByMonth);

      // Top events by registrations
      const eventRegCounts = {};
      registrations.forEach(reg => {
        if (!eventRegCounts[reg.event_id]) {
          eventRegCounts[reg.event_id] = {
            id: reg.event_id,
            title: reg.events?.title || 'Unknown',
            count: 0,
            revenue: 0,
            date: reg.events?.start_date
          };
        }
        eventRegCounts[reg.event_id].count++;
        if (reg.payment_status === 'paid') {
          eventRegCounts[reg.event_id].revenue += reg.amount_paid || 0;
        }
      });

      const topEvents = Object.values(eventRegCounts)
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Fetch page view analytics — filtered to this church's events only
      let totalPageViews = 0;
      let uniqueVisitors = 0;
      let viewsOverTime = [];

      try {
        if (eventIds.length > 0) {
          const days = dateRange === 'all' ? 365 : parseInt(dateRange) || 30;
          const startDate = new Date();
          startDate.setDate(startDate.getDate() - days);

          const { data: viewsData } = await supabase
            .from('event_views')
            .select('event_id, user_id, session_id, viewed_at')
            .in('event_id', eventIds)
            .gte('viewed_at', startDate.toISOString());

          if (viewsData) {
            totalPageViews = viewsData.length;

            // Count unique visitors by distinct session_id or user_id
            const uniqueIds = new Set();
            viewsData.forEach(v => {
              uniqueIds.add(v.user_id || v.session_id);
            });
            uniqueVisitors = uniqueIds.size;

            // Group views by day for the chart
            const viewsByDay = {};
            viewsData.forEach(v => {
              const day = new Date(v.viewed_at).toISOString().split('T')[0];
              viewsByDay[day] = (viewsByDay[day] || 0) + 1;
            });

            viewsOverTime = Object.entries(viewsByDay)
              .sort(([a], [b]) => a.localeCompare(b))
              .slice(-30)
              .map(([day, count]) => ({
                date: new Date(day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                views: count
              }));
          }
        }
      } catch (err) {
        console.error('Error fetching view analytics:', err);
      }

      // Calculate conversion rate (registrations / page views)
      const conversionRate = totalPageViews > 0 
        ? ((registrations.length / totalPageViews) * 100).toFixed(2)
        : 0;

      setAnalytics({
        totalEvents: eventsList.length,
        totalRegistrations: registrations.length,
        totalRevenue,
        avgRegistrationsPerEvent: eventsList.length > 0 ? Math.round(registrations.length / eventsList.length * 10) / 10 : 0,
        eventsByStatus,
        registrationsByMonth,
        revenueByMonth,
        topEvents,
        totalPageViews,
        uniqueVisitors,
        viewsOverTime,
        conversionRate,
        avgTicketPrice,
        growthRate
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStartDate = (range) => {
    const now = new Date();
    switch (range) {
      case '7d': return new Date(now.setDate(now.getDate() - 7));
      case '30d': return new Date(now.setDate(now.getDate() - 30));
      case '90d': return new Date(now.setDate(now.getDate() - 90));
      case '1y': return new Date(now.setFullYear(now.getFullYear() - 1));
      default: return new Date(0);
    }
  };

  const groupByMonth = (items, dateField) => {
    const months = {};
    items.forEach(item => {
      const date = new Date(item[dateField]);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      months[key] = (months[key] || 0) + 1;
    });

    return Object.entries(months)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([month, count]) => ({
        month: formatMonth(month),
        count
      }));
  };

  const groupRevenueByMonth = (registrations) => {
    const months = {};
    registrations
      .filter(r => r.payment_status === 'paid')
      .forEach(reg => {
        const date = new Date(reg.created_at);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        months[key] = (months[key] || 0) + (reg.amount_paid || 0);
      });

    return Object.entries(months)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([month, amount]) => ({
        month: formatMonth(month),
        amount
      }));
  };

  const calculateGrowthRate = (data) => {
    if (data.length < 2) return 0;
    const current = data[data.length - 1]?.count || 0;
    const previous = data[data.length - 2]?.count || 0;
    if (previous === 0) return 0;
    return (((current - previous) / previous) * 100).toFixed(1);
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const exportData = () => {
    const csvData = [
      ['Metric', 'Value'],
      ['Total Events', analytics.totalEvents],
      ['Total Registrations', analytics.totalRegistrations],
      ['Total Revenue', analytics.totalRevenue],
      ['Average Registrations per Event', analytics.avgRegistrationsPerEvent],
      ['Page Views', analytics.totalPageViews],
      ['Unique Visitors', analytics.uniqueVisitors],
      ['Conversion Rate', `${analytics.conversionRate}%`],
      ['Average Ticket Price', analytics.avgTicketPrice],
      ['Growth Rate', `${analytics.growthRate}%`]
    ];

    const csv = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const maxRegistrations = Math.max(...analytics.registrationsByMonth.map(m => m.count), 1);
  const maxRevenue = Math.max(...analytics.revenueByMonth.map(m => m.amount), 1);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Track your event performance, revenue, and engagement metrics.
          </p>
        </div>
        <div className="flex gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="1y">Last Year</option>
            <option value="all">All Time</option>
          </select>
          <button
            onClick={exportData}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export
          </button>
        </div>
      </div>

      {/* Key Metrics - Better Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Events"
          value={analytics.totalEvents}
          icon="calendar"
          color="purple"
        />
        <MetricCard
          title="Total Registrations"
          value={analytics.totalRegistrations}
          icon="users"
          color="blue"
          subtitle={`${analytics.avgRegistrationsPerEvent} avg per event`}
        />
        <MetricCard
          title="Total Revenue"
          value={formatCurrency(analytics.totalRevenue)}
          icon="dollar"
          color="emerald"
          subtitle={`${formatCurrency(analytics.avgTicketPrice)} avg ticket`}
        />
        <MetricCard
          title="Conversion Rate"
          value={`${analytics.conversionRate}%`}
          icon="trending"
          color="indigo"
          subtitle={`${analytics.totalPageViews.toLocaleString()} page views`}
          trend={parseFloat(analytics.growthRate)}
        />
      </div>

      {/* Insights Section */}
      {analytics.totalEvents > 0 && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-6 border border-purple-100 dark:border-purple-800">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/50 rounded-lg">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Key Insights</h3>
              <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                {analytics.growthRate > 0 && (
                  <li>✅ Registrations are up {analytics.growthRate}% compared to last period</li>
                )}
                {analytics.conversionRate > 2 && (
                  <li>✅ Your conversion rate of {analytics.conversionRate}% is above average</li>
                )}
                {analytics.conversionRate < 1 && (
                  <li>⚠️ Low conversion rate - consider improving event descriptions or pricing</li>
                )}
                {analytics.avgRegistrationsPerEvent < 5 && analytics.totalEvents > 0 && (
                  <li>💡 Average registrations are low - try promoting events more actively</li>
                )}
                {analytics.totalPageViews > 100 && analytics.totalRegistrations === 0 && (
                  <li>⚠️ High views but no registrations - review your registration process</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Registrations Chart */}
        <ChartCard title="Registrations Trend" subtitle="Last 6 months">
          {analytics.registrationsByMonth.length === 0 ? (
            <EmptyState message="No registration data available" />
          ) : (
            <BarChart data={analytics.registrationsByMonth} max={maxRegistrations} color="purple" />
          )}
        </ChartCard>

        {/* Revenue Chart */}
        <ChartCard title="Revenue Trend" subtitle="Last 6 months">
          {analytics.revenueByMonth.length === 0 ? (
            <EmptyState message="No revenue data available" />
          ) : (
            <BarChart 
              data={analytics.revenueByMonth.map(item => ({ month: item.month, count: item.amount }))} 
              max={maxRevenue} 
              color="emerald"
              prefix="$"
            />
          )}
        </ChartCard>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Events by Status */}
        <ChartCard title="Events by Status" subtitle={`${analytics.totalEvents} total events`}>
          {Object.keys(analytics.eventsByStatus).length === 0 ? (
            <EmptyState message="No events yet" />
          ) : (
            <div className="space-y-4">
              {Object.entries(analytics.eventsByStatus).map(([status, count]) => {
                const total = analytics.totalEvents;
                const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                const colors = {
                  pending: { bg: 'bg-yellow-500', text: 'text-yellow-700', light: 'bg-yellow-100' },
                  upcoming: { bg: 'bg-green-500', text: 'text-green-700', light: 'bg-green-100' },
                  confirmed: { bg: 'bg-green-500', text: 'text-green-700', light: 'bg-green-100' },
                  ongoing: { bg: 'bg-blue-500', text: 'text-blue-700', light: 'bg-blue-100' },
                  past: { bg: 'bg-gray-400', text: 'text-gray-700', light: 'bg-gray-100' },
                  cancelled: { bg: 'bg-red-500', text: 'text-red-700', light: 'bg-red-100' }
                };
                const color = colors[status] || { bg: 'bg-gray-500', text: 'text-gray-700', light: 'bg-gray-100' };
                
                return (
                  <div key={status} className="flex items-center gap-3">
                    <div className={`w-10 h-10 ${color.light} rounded-lg flex items-center justify-center`}>
                      <span className={`text-sm font-bold ${color.text}`}>{count}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="capitalize font-medium text-gray-700 dark:text-gray-300">{status}</span>
                        <span className="text-gray-500">{percentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${color.bg} transition-all duration-500`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ChartCard>

        {/* Top Events */}
        <ChartCard title="Top Performing Events" subtitle="By registrations">
          {analytics.topEvents.length === 0 ? (
            <EmptyState message="No registration data yet" />
          ) : (
            <div className="space-y-3">
              {analytics.topEvents.map((event, index) => (
                <div key={event.id} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-500 text-white rounded-lg flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{event.title}</p>
                    <p className="text-xs text-gray-500">{event.count} registrations • {formatCurrency(event.revenue)}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(event.revenue / event.count)} avg
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ChartCard>
      </div>
    </div>
  );
};

// Reusable Components
const MetricCard = ({ title, value, icon, color, subtitle, trend }) => {
  const icons = {
    calendar: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
    users: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
    dollar: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    trending: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6'
  };

  const colors = {
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600',
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600',
    emerald: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600',
    indigo: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600'
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>
          )}
          {trend !== undefined && trend !== 0 && (
            <div className={`inline-flex items-center gap-1 mt-2 text-xs font-medium ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d={trend > 0 ? "M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" : "M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z"} clipRule="evenodd" />
              </svg>
              {Math.abs(trend)}%
            </div>
          )}
        </div>
        <div className={`p-3 ${colors[color]} rounded-lg`}>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icons[icon]} />
          </svg>
        </div>
      </div>
    </div>
  );
};

const ChartCard = ({ title, subtitle, children }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
    <div className="mb-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
      {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const BarChart = ({ data, max, color, prefix = '' }) => {
  const colors = {
    purple: 'bg-purple-500',
    emerald: 'bg-emerald-500',
    blue: 'bg-blue-500',
    indigo: 'bg-indigo-500'
  };

  return (
    <div className="h-48 flex items-end justify-between gap-2">
      {data.map((item, index) => (
        <div key={index} className="flex-1 flex flex-col items-center group">
          <div className="w-full flex flex-col items-center relative">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {prefix}{Math.round(item.count)}
            </span>
            <div
              className={`w-full ${colors[color]} rounded-t-lg transition-all duration-500 hover:opacity-80 cursor-pointer`}
              style={{ height: `${Math.max((item.count / max) * 140, 4)}px` }}
              title={`${item.month}: ${prefix}${Math.round(item.count)}`}
            />
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400 mt-2 truncate w-full text-center">{item.month}</span>
        </div>
      ))}
    </div>
  );
};

const EmptyState = ({ message }) => (
  <div className="h-48 flex flex-col items-center justify-center text-gray-400">
    <svg className="w-12 h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
    <p className="text-sm">{message}</p>
  </div>
);

export default ChurchAnalyticsPageNew;
